import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import InboundEmailsTab from '../components/InboundEmailsTab';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Mail, RefreshCw, Loader2, Plus, Pencil, Trash2,
  AlertTriangle, CheckCircle2, EyeOff, HelpCircle,
  Flag, Filter, Inbox, ExternalLink, Send, Paperclip, ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  gmail: { label: 'Gmail', icon: Mail, color: 'bg-red-50 text-red-700 border-red-200' },
  forward: { label: 'Forward', icon: Send, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
};

const ROUTE_META = {
  invoice: { label: 'Invoice', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  debts: { label: 'Debt', color: 'bg-red-100 text-red-700 border-red-200' },
  debts_unmatched: { label: 'Debt (unmatched)', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  manual: { label: 'Manual', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  unrouted: { label: 'Unrouted', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const CLASSIFICATION_CONFIG = {
  flagged: { label: 'Flagged', icon: Flag, color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  processed: { label: 'Processed', icon: CheckCircle2, color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  ignored: { label: 'Ignored', icon: EyeOff, color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
  unclassified: { label: 'Unclassified', icon: HelpCircle, color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};

const RULE_TYPES = [
  { value: 'ignore', label: 'Ignore', desc: 'Skip these emails silently' },
  { value: 'process', label: 'Process', desc: 'Store and track these emails' },
  { value: 'flag', label: 'Flag', desc: 'Alert — needs attention' },
];

export default function EmailPage() {
  const { token, api } = useAuth();
  const [status, setStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState('all');
  const [ruleDialog, setRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '', type: 'process',
    sender_patterns: '', subject_keywords: '', action: 'process',
  });

  const [connectScope, setConnectScope] = useState('company');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [pollSettings, setPollSettings] = useState(null);
  const [savingPoll, setSavingPoll] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, unifiedRes, summaryRes, rulesRes, pollRes] = await Promise.all([
        api.get('/email/status'),
        api.get('/messages?limit=200'),
        api.get('/messages/summary'),
        api.get('/email/rules'),
        api.get('/email/poll-settings').catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setEmails(unifiedRes.data.items || []);
      setSummary(summaryRes.data);
      setRules(rulesRes.data);
      setPollSettings(pollRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const connectGmail = async () => {
    try {
      const res = await api.get(`/email/connect?scope=${connectScope}`);
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to connect');
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail?')) return;
    try {
      await api.delete('/email/disconnect');
      toast.success('Gmail disconnected');
      fetchAll();
    } catch { toast.error('Failed to disconnect'); }
  };

  const fetchEmails = async () => {
    setFetching(true);
    try {
      const res = await api.post('/email/fetch');
      toast.success(`Fetched ${res.data.fetched} emails (${res.data.flagged} flagged, ${res.data.ignored} ignored)`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch');
    }
    finally { setFetching(false); }
  };

  const savePollInterval = async (minutes) => {
    setSavingPoll(true);
    try {
      await api.put('/email/poll-settings', { poll_interval_minutes: Number(minutes) });
      toast.success(minutes === 0 ? 'Auto-pull turned off' : `Gmail will auto-pull every ${minutes} minutes`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    finally { setSavingPoll(false); }
  };

  const reclassify = async (emailId, classification) => {
    try {
      await api.put(`/emails/${emailId}/reclassify`, { classification });
      toast.success('Reclassified');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        name: rule.name,
        type: rule.type,
        sender_patterns: (rule.conditions?.sender_patterns || []).join(', '),
        subject_keywords: (rule.conditions?.subject_keywords || []).join(', '),
        action: rule.action,
      });
    } else {
      setEditingRule(null);
      setRuleForm({ name: '', type: 'process', sender_patterns: '', subject_keywords: '', action: 'process' });
    }
    setRuleDialog(true);
  };

  const saveRule = async () => {
    if (!ruleForm.name.trim()) { toast.error('Rule name required'); return; }
    const payload = {
      name: ruleForm.name,
      type: ruleForm.type,
      action: ruleForm.action,
      conditions: {
        sender_patterns: ruleForm.sender_patterns.split(',').map(s => s.trim()).filter(Boolean),
        subject_keywords: ruleForm.subject_keywords.split(',').map(s => s.trim()).filter(Boolean),
      },
    };
    try {
      if (editingRule) {
        await api.put(`/email/rules/${editingRule.id}`, payload);
      } else {
        await api.post('/email/rules', payload);
      }
      toast.success(editingRule ? 'Rule updated' : 'Rule created');
      setRuleDialog(false);
      fetchAll();
    } catch { toast.error('Failed to save rule'); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await api.delete(`/email/rules/${id}`);
      toast.success('Rule deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const toggleRule = async (rule) => {
    try {
      await api.put(`/email/rules/${rule.id}`, { is_active: !rule.is_active });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const filteredEmails = emails.filter(e => {
    if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
    if (filter !== 'all' && e.classification !== filter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Integration</h1>
          <p className="text-muted-foreground text-sm">Connect Gmail to automatically classify and process business emails</p>
        </div>
        {status?.connected && (
          <Button onClick={fetchEmails} disabled={fetching} data-testid="fetch-emails-btn">
            {fetching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Fetch New Emails
          </Button>
        )}
      </div>

      {/* Connection Status */}
      <Card data-testid="email-connection-card">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status?.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Mail className={`w-5 h-5 ${status?.connected ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="font-medium text-sm">
                Gmail {status?.connected ? (
                  <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">Connected</Badge>
                ) : (
                  <Badge variant="outline" className="ml-2">Not connected</Badge>
                )}
              </p>
              {status?.connected ? (
                <p className="text-xs text-muted-foreground">
                  {status.email_address}
                  {status.scope === 'company' && <Badge variant="outline" className="ml-1 text-[10px] h-4 bg-blue-50 text-blue-600 border-blue-200">Company-wide</Badge>}
                  {status.scope === 'personal' && <Badge variant="outline" className="ml-1 text-[10px] h-4">Personal</Badge>}
                  {status.connected_by && <span className="ml-1">connected by {status.connected_by}</span>}
                  {' — '}{status.emails_processed || 0} emails processed
                  {status.last_polled_at && ` — last checked ${new Date(status.last_polled_at).toLocaleString('en-GB')}`}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {status?.configured
                    ? 'Connect your Gmail to start processing emails'
                    : 'Gmail integration requires Google OAuth2 credentials in .env'}
                </p>
              )}
            </div>
          </div>
          {status?.connected && pollSettings && (
            <div className="flex items-center gap-2" data-testid="poll-interval-row">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Auto-pull</Label>
              <Select
                value={String(pollSettings.poll_interval_minutes ?? 15)}
                onValueChange={(v) => savePollInterval(Number(v))}
                disabled={savingPoll}
              >
                <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="poll-interval-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Off (manual only)</SelectItem>
                  {(pollSettings.allowed_intervals || [5,10,15,30,60,120,240]).filter(n => n > 0).map(n => (
                    <SelectItem key={n} value={String(n)}>
                      {n < 60 ? `Every ${n} min` : `Every ${n/60}h`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pollSettings.last_run_at && (
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                  last auto-pull {new Date(pollSettings.last_run_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
          {status?.connected ? (
            <Button variant="outline" size="sm" onClick={disconnectGmail} data-testid="disconnect-gmail-btn">
              Disconnect
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Select value={connectScope} onValueChange={setConnectScope}>
                <SelectTrigger className="w-36 h-9 text-xs" data-testid="scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company-wide</SelectItem>
                  <SelectItem value="personal">Personal only</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={connectGmail} disabled={!status?.configured} data-testid="connect-gmail-btn">
                <Mail className="w-4 h-4 mr-1" /> Connect Gmail
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { key: 'total', label: 'Total', value: summary.total, color: 'text-foreground' },
            { key: 'flagged', label: 'Flagged', value: summary.flagged, color: 'text-red-600' },
            { key: 'processed', label: 'Processed', value: summary.processed, color: 'text-blue-600' },
            { key: 'ignored', label: 'Ignored', value: summary.ignored, color: 'text-gray-500' },
            { key: 'unclassified', label: 'Unclassified', value: summary.unclassified, color: 'text-amber-600' },
          ].map(s => (
            <Card key={s.key} className="cursor-pointer hover:border-primary/50" onClick={() => setFilter(s.key === 'total' ? 'all' : s.key)}>
              <CardContent className="py-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs: Inbox & Rules */}
      <Tabs defaultValue="inbox">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="inbox-tab"><Inbox className="w-4 h-4 mr-1" /> Inbox</TabsTrigger>
          <TabsTrigger value="rules" data-testid="rules-tab"><Filter className="w-4 h-4 mr-1" /> Rules</TabsTrigger>
        </TabsList>

        {/* Inbox Tab */}
        <TabsContent value="inbox" className="space-y-3 mt-4">
          {/* Source + classification filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-muted-foreground mr-1">Source:</span>
            {['all', 'gmail', 'forward'].map(s => (
              <Button key={s} variant={sourceFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setSourceFilter(s)} data-testid={`src-${s}`}>
                {s === 'all' ? 'All' : SOURCE_META[s].label}
                {s !== 'all' && summary?.by_source && <span className="ml-1 opacity-70">({summary.by_source[s] || 0})</span>}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground ml-2 mr-1">Status:</span>
            {['all', 'flagged', 'processed', 'unclassified', 'ignored'].map(f => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
                {f === 'all' ? 'All' : CLASSIFICATION_CONFIG[f]?.label || f}
                {f !== 'all' && summary && <span className="ml-1 opacity-70">({summary[f] || 0})</span>}
              </Button>
            ))}
          </div>

          {filteredEmails.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Inbox className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  {status?.connected ? 'No emails match — try clearing filters, or connect a forwarding alias for supplier emails.' : 'Connect Gmail or send test emails to your inbound alias to get started.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredEmails.map(email => {
                const cls = CLASSIFICATION_CONFIG[email.classification] || CLASSIFICATION_CONFIG.unclassified;
                const ClsIcon = cls.icon;
                const src = SOURCE_META[email.source] || SOURCE_META.gmail;
                const SrcIcon = src.icon;
                const routeMeta = email.routed_to && ROUTE_META[email.routed_to];
                return (
                  <Card key={`${email.source}-${email.id}`} className={`${email.classification === 'ignored' ? 'opacity-50' : ''}`} data-testid={`email-${email.id}`}>
                    <CardContent className="flex items-start gap-3 py-3">
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${cls.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-sm font-medium truncate">{email.from_address}</span>
                          <Badge variant="outline" className={`text-[10px] h-4 shrink-0 ${src.color}`} data-testid={`src-badge-${email.id}`}>
                            <SrcIcon className="w-3 h-3 mr-0.5" />{src.label}
                          </Badge>
                          <Badge variant="outline" className={`text-[10px] h-4 shrink-0 ${cls.color}`}>
                            <ClsIcon className="w-3 h-3 mr-0.5" />{cls.label}
                          </Badge>
                          {routeMeta && (
                            <Badge variant="outline" className={`text-[10px] h-4 shrink-0 ${routeMeta.color}`}>
                              <ArrowRight className="w-3 h-3 mr-0.5" />{routeMeta.label}
                            </Badge>
                          )}
                          {email.has_attachments && <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />}
                        </div>
                        <p className="text-sm truncate">{email.subject}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{email.snippet}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {email.source === 'gmail' ? (
                          <Select onValueChange={(v) => reclassify(email.id, v)}>
                            <SelectTrigger className="h-7 w-24 text-xs" data-testid={`reclassify-${email.id}`}>
                              <SelectValue placeholder="Move to..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flagged">Flag</SelectItem>
                              <SelectItem value="processed">Process</SelectItem>
                              <SelectItem value="ignored">Ignore</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic px-2">via forward</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rules" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Rules classify incoming emails. Ignore rules run first, then flag, then process.
            </p>
            <Button size="sm" onClick={() => openRuleDialog()} data-testid="add-rule-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Rule
            </Button>
          </div>

          {rules.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Filter className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No rules configured. Connect Gmail to get started with default rules.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {rules.map(rule => {
                const typeCfg = RULE_TYPES.find(t => t.value === rule.type) || RULE_TYPES[1];
                const patterns = [
                  ...(rule.conditions?.sender_patterns || []).map(p => `from: ${p}`),
                  ...(rule.conditions?.subject_keywords || []).map(k => `subject: "${k}"`),
                ].slice(0, 4);
                return (
                  <Card key={rule.id} data-testid={`rule-${rule.id}`}>
                    <CardContent className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Switch checked={rule.is_active} onCheckedChange={() => toggleRule(rule)} data-testid={`toggle-rule-${rule.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${!rule.is_active ? 'text-muted-foreground' : ''}`}>{rule.name}</span>
                            <Badge variant="outline" className={`text-[10px] h-4 ${
                              rule.type === 'flag' ? 'text-red-600 bg-red-50' :
                              rule.type === 'ignore' ? 'text-gray-500 bg-gray-50' :
                              'text-blue-600 bg-blue-50'
                            }`}>{typeCfg.label}</Badge>
                            {rule.is_default && <Badge variant="outline" className="text-[10px] h-4 text-muted-foreground">Default</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {patterns.join(' | ') || 'No conditions set'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openRuleDialog(rule)} data-testid={`edit-rule-${rule.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteRule(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rule Dialog */}
      <Dialog open={ruleDialog} onOpenChange={setRuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'New Rule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Rule Name</Label>
              <Input value={ruleForm.name} onChange={(e) => setRuleForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Ignore LinkedIn notifications" data-testid="rule-name-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={ruleForm.type} onValueChange={(v) => setRuleForm(f => ({ ...f, type: v, action: v }))}>
                <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label} — {t.desc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div>
              <Label>Sender Patterns <span className="text-muted-foreground font-normal">(comma-separated, * = wildcard)</span></Label>
              <Textarea
                value={ruleForm.sender_patterns}
                onChange={(e) => setRuleForm(f => ({ ...f, sender_patterns: e.target.value }))}
                placeholder="e.g. *@linkedin.com, noreply@*, *@facebook.com"
                rows={2}
                data-testid="rule-sender-input"
              />
            </div>
            <div>
              <Label>Subject Keywords <span className="text-muted-foreground font-normal">(comma-separated)</span></Label>
              <Textarea
                value={ruleForm.subject_keywords}
                onChange={(e) => setRuleForm(f => ({ ...f, subject_keywords: e.target.value }))}
                placeholder="e.g. overdue, payment reminder, invoice"
                rows={2}
                data-testid="rule-subject-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveRule} data-testid="save-rule-btn">
              {editingRule ? 'Save Changes' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
